import { CloudProviders } from '@ariksa/cloud-account/api';
import { includes, isEmpty } from 'lodash';

import { CloudAccount } from 'containers/App/types';

import { UserPermissionConfig } from './types';

export const isAwsAccount = (account: CloudAccount) =>
  !isEmpty(account) ? account?.cloud_type === CloudProviders.Aws : true;

export const PermissionConfig = (
  cloudProvider?: string[],
  orgName?: string,
  hostName?: string,
) =>
  ({
    // route permissions
    '/': () => true,
    '/home': ({ ariksa }) => ariksa.tenancy.view(),
    '/home/ai-assistant': ({ ariksa }) => ariksa.tenancy.view(),
    '/meta': ({ ariksa }) => ariksa.tenancy.view(),
    '/dashboard': ({ ariksa }) => ariksa.tenancy.view(),
    '/dashboard/overview': ({ ariksa }) => ariksa.tenancy.view(),
    '/dashboard/data': ({ ariksa }) => ariksa.tenancy.view(),
    '/dashboard/identity': ({ ariksa }) =>
      ariksa.tenancy.view() && includes(cloudProvider, 'AWS'),
    '/dashboard/vulnerability': ({ ariksa }) => ariksa.tenancy.view(),
    '/dashboard/infrastructure': ({ ariksa }) => ariksa.tenancy.view(),
    '/documentation': () => true,
    '/documentation/:chapterSlug': () => true,

    '/findings': ({ ariksa }) => ariksa.tenancy.view(),
    '/findings/overview': ({ ariksa }) => ariksa.tenancy.view(),
    '/findings/alerts': ({ ariksa }) => ariksa.tenancy.view(),
    '/findings/issue/:id': ({ ariksa }) => ariksa.tenancy.view(),
    '/findings/tickets': ({ ariksa }) => ariksa.tenancy.view(),
    '/findings/alerts/:status/:severity': ({ ariksa }) => ariksa.tenancy.view(),

    '/user-profile': () => true,
    '/user-profile/profile': () => true,
    '/user-profile/permissions': () => true,
    '/user-profile/timeline': () => true,
    '/user-profile/sessions': () => true,
    '/settings/:type': () => true,

    // inventory permissions
    '/inventory': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/cloud': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/cloud/resource/:resource_type/:id': ({ ariksa }) =>
      ariksa.tenancy.view(),
    '/inventory/software': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/allowlist': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/allowlist/add': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/allowlist/edit/:uuid': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/context': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/context/add': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/inventory/context/edit/:uuid': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },

    // visibility permissions
    '/visibility': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/visibility/security-graph': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },
    '/visibility/vulnerability': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/vulnerability/resource/:id': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/data': ({ ariksa: { tenancy }, cloud: { remediation } }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/jit-requests': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      //return false;
      return tenancy.view() || remediation.view('Any');
    },
    '/jit/requests': ({ ariksa: { tenancy }, cloud: { remediation } }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/activity': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      //return false;

      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/activity/group/add': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => tenancy.view() || remediation.view('Any'),
    '/visibility/activity/group/edit/:id': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => tenancy.view() || remediation.view('Any'),
    '/visibility/activity/group/:group_id': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/activity/group/:group_id/resource/:resource_id': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/activity/group/:group_id/resource/:resource_id/events': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/activity/resource/:id': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      //return false;
      return tenancy.view() || remediation.view('Any');
    },
    '/visibility/activity/resource/events/:id': ({
      ariksa: { tenancy },
      cloud: { remediation },
    }) => {
      //return false;
      return tenancy.view() || remediation.view('Any');
    },

    // policy-hub
    '/policy-hub': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/blueprint': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/blueprint/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/blueprint/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },

    '/policy-hub/policy/cloud': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/policy/cloud/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/policy/cloud/add/new': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/policy/cloud/edit/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },

    '/policy-hub/policy/data': up => {
      const { cloudResources } = up;
      return (
        hostName !== 'app.ariksa.io' &&
        (!!cloudResources.size ||
          up.cloud.policy.view('Any') ||
          up.cloud.reporting.view('Any'))
      );
    },
    '/policy-hub/policy/data/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/policy/data/data-group/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/policy/data/data-group/edit/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/policy/data/edit/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/catalog': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/policy': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/policy/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/policy/edit/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/group': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/group/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/jit/data/group/edit/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },

    '/policy-hub/remediation': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/remediation/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/remediation/edit/:id': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },

    '/policy-hub/workflow': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/workflow/add': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },
    '/policy-hub/workflow/edit/:uuid': up => {
      const { cloudResources } = up;
      return (
        !!cloudResources.size ||
        up.cloud.policy.view('Any') ||
        up.cloud.reporting.view('Any')
      );
    },

    // compliance
    '/compliance': ({
      ariksa: { policy, tenancy },
      cloud: { remediation },
    }) => {
      return policy.view() || tenancy.view() || remediation.view('Any');
    },
    '/compliance/compare-policy': ({
      ariksa: { policy, tenancy },
      cloud: { remediation },
    }) => {
      return policy.view() || tenancy.view() || remediation.view('Any');
    },
    '/compliance/resources': ({
      ariksa: { policy, tenancy },
      cloud: { remediation },
    }) => {
      return policy.view() || tenancy.view() || remediation.view('Any');
    },

    '/compliance/rules': ({
      ariksa: { policy, tenancy },
      cloud: { remediation },
    }) => {
      return policy.view() || tenancy.view() || remediation.view('Any');
    },

    // reports
    '/reports': ({ ariksa }) => ariksa.tenancy.view(),
    '/reports/all': ({ ariksa }) => ariksa.tenancy.view(),
    '/reports/dashboard': ({ ariksa }) => ariksa.tenancy.view(),
    '/reports/dashboard/edit': ({ ariksa }) => ariksa.tenancy.view(),
    '/reports/edit': ({ ariksa }) => ariksa.tenancy.view(),

    // setup
    '/setup/accounts/edit/:provider/:id': ({ ariksa }) => ariksa.tenancy.view(),
    '/setup/accounts/edit/:provider/:id/:accountType': ({ ariksa }) =>
      ariksa.tenancy.view(),
    '/setup': ({
      ariksa: { system, policy, tenancy },
      cloud: { compliance },
    }) => {
      return system.view() || tenancy.view() || compliance.view('Any');
    },

    '/setup/accounts/:uuid/member_accounts': ({ ariksa }) =>
      ariksa.tenancy.view(),
    '/setup/accounts': ({ ariksa }) => ariksa.tenancy.view(),
    '/setup/accounts/add/:provider': ({ ariksa }) => ariksa.tenancy.view(),
    '/setup/accounts/connect/:provider': ({ ariksa }) => ariksa.tenancy.view(),
    '/setup/accounts/enable-scan/:provider/:uuid': ({ ariksa }) => {
      return ariksa.tenancy.view();
    },
    '/setup/accounts/scan/data/:provider/:id': ({ ariksa }) => {
      return ariksa.tenancy.view();
    },

    '/setup/outpost': ({ ariksa: { system, tenancy } }) => {
      return tenancy.view();
    },
    '/setup/outpost/add/:type': ({ ariksa: { system, tenancy } }) => {
      return tenancy.view();
    },
    '/setup/outpost/add/vulnerability/account': ({
      ariksa: { system, tenancy },
    }) => {
      return tenancy.view();
    },

    '/setup/jit': ({ ariksa: { system, tenancy } }) => {
      return tenancy.view();
    },
    '/setup/jit/add': ({ ariksa: { system, tenancy } }) => {
      return tenancy.view();
    },
    '/setup/jit/enable-data-access/:provider/:id': ({
      ariksa: { system, tenancy },
    }) => {
      return tenancy.view();
    },
    '/setup/jit/approval-policy': ({ ariksa: { system, tenancy } }) => {
      return tenancy.view();
    },
    '/setup/jit/approval-policy/add': ({ ariksa: { system, tenancy } }) => {
      return tenancy.view();
    },
    '/setup/jit/approval-policy/edit/:uuid': ({
      ariksa: { system, tenancy },
    }) => {
      return tenancy.view();
    },
    '/jit': ({ ariksa: { tenancy } }) => tenancy.view(),
    '/jit/setup': ({ ariksa: { tenancy } }) => tenancy.view(),
    '/jit/setup/add': ({ ariksa: { tenancy } }) => tenancy.view(),
    '/jit/setup/enable-data-access/:provider/:id': ({ ariksa: { tenancy } }) =>
      tenancy.view(),
    '/jit/setup/approval-policy': ({ ariksa: { tenancy } }) => tenancy.view(),
    '/jit/setup/approval-policy/add': ({ ariksa: { tenancy } }) =>
      tenancy.view(),
    '/jit/setup/approval-policy/edit/:uuid': ({ ariksa: { tenancy } }) =>
      tenancy.view(),
    '/setup/roles': ({ ariksa: { system, tenancy } }) => {
      return false;
      // return system.view() || tenancy.view();
    },
    '/setup/roles/add': ({ ariksa: { system, tenancy } }) => {
      return system.create() || tenancy.create();
    },
    '/setup/users': ({ ariksa: { system, tenancy } }) => {
      return system.view() || tenancy.view();
    },
    '/setup/users/add': ({ ariksa: { system, tenancy } }) => {
      return system.create() || tenancy.create();
    },
    '/setup/users/edit/:user_id': ({ ariksa: { system, tenancy } }) => {
      return system.update() || tenancy.update();
    },
    '/setup/groups': ({ ariksa: { system, tenancy } }) => {
      return system.view() || tenancy.view();
    },
    '/setup/groups/add': ({ ariksa: { system, tenancy } }) => {
      return system.create() || tenancy.create();
    },
    '/setup/groups/edit/:group_id': ({ ariksa: { system, tenancy } }) => {
      return system.update() || tenancy.update();
    },
    '/setup/groups/view/:group_id': ({
      ariksa: { system, policy, tenancy },
    }) => {
      return system.view() || tenancy.view();
    },

    '/setup/ip-whitelisting': ({ ariksa: { system, tenancy } }) => {
      //WARNING(su): temporarily hidden
      return false;
      // return system.view() || tenancy.view();
    },

    '/setup/ip-whitelisting/add': ({ ariksa: { system, tenancy } }) => {
      return system.view() || tenancy.view();
    },

    '/setup/identity': ({ ariksa: { policy, tenancy } }) => {
      return policy.view() || tenancy.view();
    },

    '/setup/integrations': ({ ariksa: { policy, tenancy } }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/api-keys': ({ ariksa: { policy, tenancy } }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/:provider': ({ ariksa: { policy, tenancy } }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/sso/:provider': ({ ariksa: { policy, tenancy } }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/add/:provider': ({ ariksa: { policy, tenancy } }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/add/sso/:provider': ({
      ariksa: { policy, tenancy },
    }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/update/:provider/:id': ({
      ariksa: { policy, tenancy },
    }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/jira/update/:jira_id': ({
      ariksa: { policy, tenancy },
    }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/slack/update/:slack_id': ({
      ariksa: { policy, tenancy },
    }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/teams/update/:teams_id': ({
      ariksa: { policy, tenancy },
    }) => {
      return policy.view() || tenancy.view();
    },
    '/setup/integrations/email/update/:email_id': ({
      ariksa: { policy, tenancy },
    }) => {
      return policy.view() || tenancy.view();
    },

    '/setup/environment': ({
      ariksa: { policy, tenancy },
      cloud: { compliance },
    }) => {
      return policy.view() || tenancy.view() || compliance.view('Any');
    },
    '/setup/environment/edit/:uuid': ({
      ariksa: { policy, tenancy },
      cloud: { compliance },
    }) => {
      return policy.view() || tenancy.view() || compliance.update('Any');
    },
    '/setup/environment/add': ({
      ariksa: { policy, tenancy },
      cloud: { compliance },
    }) => {
      return policy.view() || tenancy.view() || compliance.create('Any');
    },

    '/setup/data': ({ ariksa: { policy, tenancy }, cloud: { compliance } }) => {
      return policy.view() || tenancy.view() || compliance.create('Any');
    },
    '/setup/data/add/:type': ({
      ariksa: { policy, tenancy },
      cloud: { compliance },
    }) => {
      return policy.view() || tenancy.view() || compliance.create('Any');
    },
    '/setup/data/edit/:type/:uuid': ({
      ariksa: { policy, tenancy },
      cloud: { compliance },
    }) => {
      return policy.view() || tenancy.view() || compliance.create('Any');
    },

    '/organizations': ({ ariksa: { system } }) => {
      return system.view();
    },
    '/organizations/new-organization': ({ ariksa: { system } }) => {
      return system.view();
    },
    '/organizations/edit/:org_id': ({ ariksa: { system } }) => {
      return system.view();
    },

    // event-log
    '/event-log': ({ cloud: { remediation }, ariksa }) => {
      return ariksa.tenancy.view(); // sm: hiding for now
      // return remediation.view('Any');
    },

    // vulnerability
    '/vulnerability/:uuid': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },

    // pii
    '/pii/:uuid': ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },

    resources_user_map: ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },

    resources_resource_map: ({ cloud: { remediation } }) => {
      return remediation.view('Any');
    },

    select_cloud_account: ({ ariksa: { system } }) => {
      return !system.view();
    },

    // component permissions
  } as UserPermissionConfig);
